import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircle } from 'lucide-react';
import { RedeemCreditsCashModal, type RedeemCreditsCashFormValues } from '@kioskgaming/ui';
import { dispatchShopCreditBalanceRefresh } from '../../constants';
import { postShopPlayerCredits } from '../../services/api';

export type RedeemCreditsToPlayerTriggerProps = {
  playerId: string;
  playerName: string;
  playerBalance?: number | null;
  onSuccess?: () => void;
  disabled?: boolean;
  buttonText?: string;
  className?: string;
  defaultOpen?: boolean;
};

export default function RedeemCreditsToPlayerTrigger({
  playerId,
  playerName,
  playerBalance,
  onSuccess,
  disabled = false,
  buttonText = 'Redeem',
  className = '',
  defaultOpen = false,
}: RedeemCreditsToPlayerTriggerProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (values: RedeemCreditsCashFormValues) => {
      if (playerBalance != null && values.creditAmount > playerBalance) {
        const msg = 'Redeem amount cannot exceed Player balance';
        setError(msg);
        toast.error(msg);
        return;
      }

      setSubmitting(true);
      setError(null);
      try {
        const res = await postShopPlayerCredits(playerId, {
          action: 'deduct',
          amount: values.creditAmount,
          credits: values.creditAmount,
          cash_paid: values.cashPaid,
          received_at: values.receivedAt,
          external_reference: values.externalReference,
          note: values.note,
        });
        if (!res.success) {
          const msg = res.message || 'Redeem failed';
          setError(msg);
          toast.error(msg);
          return;
        }
        toast.success('Credits redeemed');
        setOpen(false);
        dispatchShopCreditBalanceRefresh();
        onSuccess?.();

        const cashTx = (res.data?.cash_transaction ?? res.data?.cashTransaction) as
          | { id?: string; createdAt?: string }
          | undefined;
        if (cashTx?.id && cashTx?.createdAt) {
          navigate(
            `/cash-settlement/redemptions/${cashTx.id}?created_at=${encodeURIComponent(String(cashTx.createdAt))}`,
          );
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Redeem failed';
        setError(msg);
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, onSuccess, playerBalance, playerId],
  );

  const btnClass =
    'inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={`${btnClass} ${className}`}
      >
        <ArrowUpCircle className="h-4 w-4 shrink-0" aria-hidden />
        {buttonText}
      </button>

      <RedeemCreditsCashModal
        open={open}
        onClose={() => setOpen(false)}
        playerName={playerName}
        playerBalance={playerBalance}
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
      />
    </>
  );
}
